/** React Imports */

/** MUI imports */
import { FormControl, Skeleton } from '@mui/material'
import React, { cloneElement, PropsWithChildren, ReactElement } from 'react'
/** Components imports */
import { Label } from '@/components/atoms/label'

/** Constants imports */
import { FieldContext } from '../constants'
/** Types imports */
import { FieldProps } from '../types/Field.types'
import { FieldAutocompleteController } from './FieldAutocompleteController'
import { FieldCheckboxController } from './FieldCheckboxController'
import { FieldColorPickerController } from './FieldColorPickerController'
import { FieldHelperText } from './FieldHelperText'
import { FieldInput } from './FieldInput'
import { FieldInputController } from './FieldInputController'
import { FieldLabel } from './FieldLabel'
import { FieldSelectController } from './FieldSelectController'
import { FieldSwitchController } from './FieldSwitchController'
import { FieldTextEditorController } from './FieldTextEditorController'
import { FieldUploadFileController } from './FieldUploadFileController'

/**
 * The `Field` component is a reusable component designed to
 * wrap form field elements, providing additional props such as `isRequired`
 * and `error` to its children. It also allows rendering a label and helper text.
 *
 * @param {boolean} [props.isRequired=false] - Indicates if the field is required.
 * @param {boolean} [props.isCustomHelperText=false] - Indicates if custom helper text is used.
 * @param {boolean} props.error - The errors object provided by `react-hook-form`.
 * @param {string} [props.label] - The label text for the field.
 * @param {string} [props.name=''] - The name of the field, used for form control.
 * @param {string} [props.helperText] - The helper text to display for the field.
 * @param {string} [props.isLoading] - Indicates if the field is in a loading state.
 * @param {string} [props.skeletonHeight] - The height of the skeleton loading animation.
 * @param {React.ReactNode} props.children - The children components to be wrapped by the Field component.
 * @param {object} [props.otherProps] - Other props to be passed to the children components.
 *
 * @returns {React.FC} The Field component.
 *
 * @example
 * // Usage example
 * <Field
 *   isRequired={true}
 *   label="Username"
 *   fieldName="username"
 *   fieldControl={control}
 *   errors={errors}
 * >
 *   <input />
 * </Field>
 */

export const Field: React.FC<PropsWithChildren<FieldProps>> & {
  Label: typeof FieldLabel
  HelperText: typeof FieldHelperText
  Input: typeof FieldInput
  InputController: typeof FieldInputController
  SelectController: typeof FieldSelectController
  FieldAutocompleteController: typeof FieldAutocompleteController
  CheckboxController: typeof FieldCheckboxController
  SwitchController: typeof FieldSwitchController
  UploadFileController: typeof FieldUploadFileController
  TextEditorController: typeof FieldTextEditorController
  ColorPickerController: typeof FieldColorPickerController
} = ({
  isRequired = false,
  isCustomHelperText = false,
  label,
  hideLabelTop = false,
  name,
  error,
  helperText,
  gapSize = 1,
  children,
  isLoading = false,
  skeletonHeight = 40,
  ...otherProps
}) => {
  const contextValue = { isRequired, isCustomHelperText, label, name, error, helperText, ...otherProps }

  const renderChildrenWithProps = (children: React.ReactNode) => {
    return React.Children.map(children, child => {
      if (React.isValidElement(child)) {
        return cloneElement(child as ReactElement<any>, {
          isRequired,
          error,
          helperText,
          ...otherProps
        })
      }
      return child
    })
  }

  return (
    <FieldContext.Provider value={contextValue}>
      <FormControl error={error} fullWidth sx={{ gap: gapSize }}>
        {isLoading ? (
          <Skeleton variant='rectangular' width={'100%'} sx={{ borderRadius: '6px' }} height={skeletonHeight} />
        ) : (
          <>
            {label && !hideLabelTop && (
              <Label isRequired={isRequired} weight={otherProps.weight || 'bolder'}>
                {label}
              </Label>
            )}
            {renderChildrenWithProps(children)}
          </>
        )}
      </FormControl>
    </FieldContext.Provider>
  )
}

// Attach compound components
Field.Label = FieldLabel
Field.HelperText = FieldHelperText
Field.Input = FieldInput
Field.InputController = FieldInputController
Field.SelectController = FieldSelectController
Field.FieldAutocompleteController = FieldAutocompleteController
Field.CheckboxController = FieldCheckboxController
Field.SwitchController = FieldSwitchController
Field.UploadFileController = FieldUploadFileController
Field.TextEditorController = FieldTextEditorController
Field.ColorPickerController = FieldColorPickerController
